import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";

/**
 * Shared legal document shell.
 * IMPORTANT: All copy comes from i18n dictionaries under app.legal.*
 * The dictionaries contain clearly marked [PLACEHOLDER — ...] sections that
 * MUST be replaced by professionally validated legal text before public launch.
 * The layout is designed so those substitutions do not require any code change.
 */
export default function LegalDocument({ titleKey, docKey, testId }) {
  const { t, lang } = useI18n();
  const today = new Date().toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
    year: "numeric", month: "long", day: "numeric",
  });
  const doc = t(`app.legal.${docKey}`);
  const sections = Array.isArray(doc?.sections) ? doc.sections : [];

  return (
    <div className="min-h-screen flex flex-col" data-testid={testId}>
      <MarketingHeader />
      <div className="nvl-container py-12 md:py-20 flex-1">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-6"
          style={{ color: "hsl(var(--nvl-ink-soft))" }}
        >
          <ArrowLeft size={14} /> {t("app.legal.backHome")}
        </Link>

        <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
          {t("landing.footer.privacy") /* section label reused */ }
        </p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl">
          {t(`app.legal.${titleKey}`)}
        </h1>
        <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("app.legal.lastUpdated", { date: today })}
        </p>

        <div
          className="mt-6 nvl-card p-4 md:p-5 text-sm"
          style={{ borderColor: "hsl(var(--nvl-accent))" }}
        >
          <div
            className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-widest mb-2"
            style={{
              backgroundColor: "hsl(var(--nvl-accent) / 0.15)",
              color: "hsl(var(--nvl-accent))",
            }}
          >
            {t("app.legal.placeholderBadge")}
          </div>
          <p style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.legal.draftNotice")}
          </p>
        </div>

        <p className="mt-8 text-base md:text-lg max-w-3xl">
          {doc?.intro}
        </p>

        <div className="mt-10 space-y-8 max-w-3xl">
          {sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${docKey}-${i}`}>
              <h2 className="font-display text-2xl md:text-3xl">{s.title}</h2>
              <p className="mt-3 text-sm md:text-base" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {s.body}
              </p>
            </section>
          ))}
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
